<script>
  import { createEventDispatcher, getContext } from "svelte";
  import { tooltip } from "svelte-utilities";
  import cloneDeep from "lodash/cloneDeep";
  import set from "lodash/set";
  import { downloadBlob } from "downloaders";

  import FileIcon from "@local/assets/icons/file.svg";
  import CaretRightIcon from "@local/assets/icons/caret-right.svg";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";
  import FilePdfIcon from "@local/assets/icons/file-pdf.svg";
  import FileImgIcon from "@local/assets/icons/file-img.svg";
  import DownloadIcon from "@local/assets/icons/download.svg";
  import SpinnerIcon from "@local/assets/icons/spinner.svg";
  import UploadIcon from "@local/assets/icons/upload.svg";
  import GridIcon from "@local/assets/icons/table.svg";
  import InvertIcon from "@local/assets/icons/invert.svg";

  import { Modal } from "svelte-utilities";
  import Dropzone from "#lib/dropzone/Dropzone.svelte";
  import PageList from "#lib/sidebar/PageList.svelte";

  import Viewport from "#lib/drawing/ViewportFixed.svelte";
  import DocumentThumbnail from "#lib/DocumentThumbnailMini.svelte";
  import liteDrawingMini from "@local/extensions/drawing/lite-drawing-mini.js";
  import SidebarSection from "./SidebarSection.svelte";
  import TextInput from "./TextInput.svelte";
  import SelectInput from "./SelectInput.svelte";
  import BooleanInput from "./BooleanInput.svelte";
  import DimtextInput from "./DimtextInput.svelte";
  import ButtonInput from "./ButtonInput.svelte";
  import extension from "@local/extensions/utilities/extension.js";
  import { incrementMark } from "@local/extensions/identifiers/mark.js";
  import {
    transformSheetPoint,
    sheetScaleFactor,
  } from "@local/extensions/geometry/transform-sheet-points.js";
  import {
    createClipAttachment,
    createIIIFDrawing,
    createItem as defaultItem,
    createLocation as defaultLocation,
  } from "@local/lamina-core";
  import maxima from "@local/extensions/utilities/maxima.js";
  import locationsByDocument from "#src/extensions/locations-by-document.js";
  import {
    showDocFiles,
    showDocAnnotations,
    selectedAnnotations,
    selectedPages,
    hiddenPages,
    hiddenLocations,
    documentViewShowItems,
    documentViewShowCollections,
    documentViewShowTypes,
    documentViewShowNotes,
    documentViewLocationFilter,
    documentViewItemFilter,
    documentViewCollectionFilter,
    documentViewTypeFilter,
    documentViewNoteFilter,
  } from "#src/stores/ui.js";
  import { profile } from "#src/stores/auth.js";
  import { api, getDocPdf } from "#src/api";
  import eb from "#src/extensions/event-bus.js";
  import { lowerFirst } from "lodash";
  import { alerts } from "#src/stores/alerts.js";

  export let origDoc = null;
  export let doc;
  export let group;
  export let items = [];
  export let disabled;
  export let annotations = [];
  export let hoveredFile = null;
  export let hoveredPage = null;
  export let lastSelectedPage = null;
  export let hoveredAnnotation = null;
  export let viewer = null;
  export let filters = null;

  const dispatch = createEventDispatcher();
  const fileData = getContext("fileData");
  const typeColors = getContext("typeColors");

  let convertModal;
  let annoSelector;
  let overallAnnoSelection;
  let thumbnailModal;
  let thumbnailDropzone;

  let initialMark = "";
  let importedDimensionIs = "width";
  let coalesceLinearItems = false;
  let alternateDim;
  let createClipsFromImportedShapes = false;
  let setImportedClipAsDrawing = false;
  let downloading = false;

  let importAs = "items";
  let showImportAs = false;

  function setImportAs(value) {
    importAs = value;
    showImportAs = false;
  }

  const fileIcons = {
    file: FileIcon,
    pdf: FilePdfIcon,
    jpg: FileImgIcon,
    jpeg: FileImgIcon,
    png: FileImgIcon,
    svg: FileImgIcon,
  };

  const extensions = {
    "image/jpeg": "jpg",
    "image/png": "png",
  };

  $: isVirtual = origDoc && doc?.id !== origDoc.id;
  $: docLocations = locationsByDocument($group);
  $: locations = docLocations[doc?.id] || [];
  $: checkSelectionState($selectedAnnotations, annoSelector, annotations);
  $: selectedAnnos = annotations.filter((a) => $selectedAnnotations[a.id]);
  $: files = getPresentFiles(doc, $fileData);
  $: pageIds = getPageIds(doc);
  $: fileIndex = getFileIndex(doc);
  $: docFilters = getDocFilters(
    filters,
    doc,
    $documentViewShowItems,
    $documentViewShowCollections,
    $documentViewShowTypes,
    $documentViewShowNotes,
    $documentViewLocationFilter,
    $documentViewItemFilter,
    $documentViewCollectionFilter,
    $documentViewTypeFilter,
    $documentViewNoteFilter,
    $hiddenPages,
    $hiddenLocations,
  );

  function getDocFilters(
    filters,
    doc,
    show_items,
    show_collections,
    show_types,
    show_notes,
    location_filter,
    item_filter,
    collection_filter,
    type_filter,
    note_filter,
    hidden_pages,
    hidden_locations,
  ) {
    if (filters === "saved") {
      return doc?.data?.filters;
    } else if (filters === "active") {
      return {
        show_items,
        show_collections,
        show_types,
        show_notes,
        location_filter,
        item_filter,
        collection_filter,
        type_filter,
        note_filter,
        hidden_pages,
        hidden_locations,
      };
    } else {
      return null;
    }
  }

  function updateAnnotationDimension() {
    dispatch("update-annotations", {
      importedDim: importedDimensionIs,
      alternateDim,
    });
  }

  function getPresentFiles(doc, fd) {
    if (!doc?.data?.pages) return [];
    const f = doc.data.pages.order.reduce((files, pageIndex) => {
      const page = doc.data.pages[pageIndex];
      files[page.name] = true;
      return files;
    }, {});
    return Object.keys(f)
      .map((f) => fd[f])
      .filter((f) => f);
  }

  function getPageIds(doc) {
    const indices = {};
    doc?.data.pages?.order
      .filter((pageId) => !doc.data.pages[pageId].hidden)
      .forEach((pageId) => {
        const page = doc.data.pages[pageId];
        if (!indices[page.name]) indices[page.name] = [];
        indices[page.name].push(pageId);
      });
    return indices;
  }

  function getFileIndex(doc) {
    const idx = {};
    doc?.data.pages.order.forEach((pageId) => {
      const page = doc.data.pages[pageId];
      if (!idx[page.name]) idx[page.name] = {};
      idx[page.name][page.page] = pageId;
    });
    return idx;
  }

  function checkSelectionState(selected, annoSelector, annotations) {
    if (!annotations) return;
    const ids = annotations.map((a) => a.id);
    if (ids.every((id) => selected[id])) {
      overallAnnoSelection = true;
    } else if (ids.every((id) => !selected[id])) {
      overallAnnoSelection = false;
    } else {
      overallAnnoSelection = "mixed";
    }

    if (annoSelector) {
      if (overallAnnoSelection === "mixed") {
        annoSelector.indeterminate = true;
      } else {
        annoSelector.indeterminate = false;
      }
    }
  }

  function updateDoc(prop, value) {
    const id = isVirtual ? origDoc.id : doc.id;
    if (typeof id === "string") {
      group.updateDocument(id, { [prop]: value });
    }
  }

  async function downloadFile(file) {
    try {
      const { data, error } = await api.storage.from("documents").download(file.name);
      if (error) throw error;

      const filename = file.file.user_metadata?.name || file.name;
      downloadBlob(data, filename);
    } catch (error) {
      console.log(error);
    }
  }

  function beginConverting() {
    convertModal.open();
  }

  function convertAnnotations() {
    const converted = annotations.filter((a) => $selectedAnnotations[a.id]);

    // Create items
    let newItems = [];
    let newLocs = [];
    const usedLinearDims = {};
    const indexedItems = {};

    let mark = initialMark || null;
    converted.forEach((a, i) => {
      const item = {
        ...defaultItem($group.id, $profile.id, [...items, ...newItems]),
        ...a.item,
        is_imported: true,
        is_collection: importAs === "openings",
      };

      if (a?.notes?.length > 0) {
        const { note_label } = a.notes[0];
        if (typeof note_label === "string" && note_label.length > 0) {
          item.mark = note_label;
        }
      }

      let useItem = true;
      if (a.type === "/LineDimension" && coalesceLinearItems) {
        const dim = importedDimensionIs === "width" ? a.item.width : a.item.height;
        const key = dim.toString();
        if (usedLinearDims[key]) {
          const existingItem = usedLinearDims[key];
          existingItem.quantity += 1;
          indexedItems[i] = existingItem;
          useItem = false;
        } else {
          usedLinearDims[key] = item;
          indexedItems[i] = item;
        }
      }

      if (useItem) {
        if (initialMark) {
          if (i === 0) {
            item.mark = initialMark;
          } else {
            mark = incrementMark(mark);
            item.mark = mark;
          }
        }

        newItems.push(item);
        newLocs.push(item);
      } else {
        newLocs.push(null);
      }
    });

    // Add location markers for each new item
    const locs = newLocs
      .map((maybeItem, i) => {
        const item = maybeItem || indexedItems[i];
        const annotation = converted[i];
        const labelPoint = annotation.own_label_point;

        const tile = viewer.tile(annotation.page);
        const size = tile.getContentSize();

        const sheetScale = sheetScaleFactor(annotation.sheet.limits, annotation.sheet.rotation, size);
        const tpt = transformSheetPoint(
          {
            x: labelPoint[0] - annotation.sheet.limits[0][0],
            y: labelPoint[1] - annotation.sheet.limits[0][1],
          },
          annotation.sheet.rotation,
          sheetScale,
          size,
        );

        const position = {
          x: tpt.x,
          y: tpt.y,
        };

        let sheetRotation = annotation.sheet.rotation % 360 || 0;

        const file_path = annotation.document_name;
        const file_page = annotation.sheet_idx;
        const page_id = fileIndex[file_path]?.[file_page];

        const location = defaultLocation(
          {
            id: crypto.randomUUID(),
            group_id: $group.id,
            document_id: doc.id,
            index: locations.length,
            type: "pin",
            position,
            page_id,
            transform: {
              scale: sheetScale,
              translate: { x: 0, y: 0 },
            },
            clip: null,
            record_type: importAs === "items" ? "item" : "collection",
            record_id: item.id,
          },
          $documentViewLocationFilter,
        );

        if (annotation.type === "/PolygonDimension") {
          const m = maxima(annotation.geom.coordinates[0]);

          let bb;
          let w;
          let h;

          if (sheetRotation === 0) {
            bb = { x: m.minX, y: m.minY };
            w = m.maxX - m.minX;
            h = m.maxY - m.minY;
          } else if (sheetRotation === 90) {
            bb = { x: m.maxX, y: m.minY };
            w = m.maxY - m.minY;
            h = m.maxX - m.minX;
          } else if (sheetRotation === 180) {
            bb = { x: m.maxX, y: m.maxY };
            w = m.maxX - m.minX;
            h = m.maxY - m.minY;
          } else if (sheetRotation === 270) {
            // TODO: CORRECT ?
            bb = { x: m.minX, y: m.maxY };
            w = m.maxY - m.minY;
            h = m.maxX - m.minX;
          } else {
            throw new Error(`Unexpected rotation value ${sheetRotation}`);
          }

          const bboxTpt = transformSheetPoint(
            { x: bb.x - annotation.sheet.limits[0][0], y: bb.y - annotation.sheet.limits[0][1] },
            annotation.sheet.rotation,
            sheetScale,
            size,
          );

          const diag = Math.sqrt(w * w + h * h);
          const itemDiag = Math.sqrt(
            item.cache.width * item.cache.width + item.cache.height * item.cache.height,
          );
          const itemScale = diag / itemDiag;

          let clip = null;
          if (createClipsFromImportedShapes) {
            const clipPoint = transformSheetPoint(bb, annotation.sheet.rotation, sheetScale, size);
            const clipWidth = sheetScale * w;
            const clipHeight = sheetScale * h;
            const clipMargin = Math.max(clipWidth, clipHeight) * 0.1;
            clip = {
              x: clipPoint.x - clipMargin,
              y: clipPoint.y - clipHeight - clipMargin,
              width: clipWidth + 2 * clipMargin,
              height: clipHeight + 2 * clipMargin,
            };
          }

          location.transform = {
            scale: sheetScale * itemScale,
            translate: bboxTpt,
          };
          location.clip = clip;
        }

        return location;
      })
      .filter((l) => l.page_id);

    if (setImportedClipAsDrawing) {
      newItems = newItems.map((item) => {
        const location = locs.find((l) => l.record_id === item.id && l.clip);
        if (location) {
          const attachment = createClipAttachment($group, location);
          item.drawing = createIIIFDrawing(attachment);
        }
        return item;
      });
    }

    if (importAs === "items" && $documentViewItemFilter) {
      if ($documentViewItemFilter.operator === "is_equal_to") {
        newItems.forEach((item) => {
          set(item, $documentViewItemFilter.field, $documentViewItemFilter.value);
        });
      }
    } else if (importAs === "openings" && $documentViewCollectionFilter) {
      if ($documentViewCollectionFilter.operator === "is_equal_to") {
        newItems.forEach((item) => {
          set(item, $documentViewCollectionFilter.field, $documentViewCollectionFilter.value);
        });
      }
    }
    group.update([
      { type: "item", action: "add", records: newItems, block: true },
      { type: "location", action: "add", records: locs },
    ]);

    $selectedAnnotations = {};
  }

  function selectFile(file) {
    const ids = doc.data.pages.order.filter((id) => doc.data.pages[id].name === file.name);
    selectedPages.selectOnly(...ids);
    lastSelectedPage = null;
  }

  function selectAllAnnotations() {
    const ids = annotations.map((a) => a.id);
    if (ids.some((id) => $selectedAnnotations[id])) {
      selectedAnnotations.deselect(...ids);
    } else {
      selectedAnnotations.select(...ids);
    }
  }

  function invertAnnotationSelection() {
    const ids = annotations.map((a) => a.id);
    const selected = ids.filter((id) => !$selectedAnnotations[id]);
    selectedAnnotations.selectOnly(...selected);
  }

  function toggleThumbnailModal() {
    thumbnailModal.open();
  }

  async function uploadThumbnail(evt) {
    const { file, data } = evt.detail;
    const id = isVirtual ? origDoc.id : doc.id;

    const uuid = crypto.randomUUID();
    const extension = extensions[file.type] || "jpg";
    const thumbName = `${id}/${uuid}-thumbnail.${extension}`;

    const stored = await api.storage.from("documents").upload(thumbName, data, {
      contentType: file.type,
    });

    if (stored.error) {
      console.error("Error uploading thumbnail:", stored.error);
      thumbnailDropzone.stopLoading();
      thumbnailModal.close();
      return;
    }

    const thumbnail = {
      bucket: "documents",
      path: stored.data.path,
      type: "image",
      name: doc.name,
    };

    const docData = isVirtual ? origDoc.data : doc.data;
    const ddata = cloneDeep(docData);
    ddata.thumbnail = thumbnail;
    group.updateDocument(id, { data: ddata });
    thumbnailDropzone.stopLoading();
    thumbnailModal.close();
  }

  async function downloadDoc() {
    downloading = true;
    const id = isVirtual ? origDoc.id : doc.id;
    const pdf = await getDocPdf(id);
    if (pdf.error) {
      downloading = false;
      alerts.add({
        type: "danger",
        message: "Error downloading PDF",
      });
      console.log(pdf.error);
      return;
    }
    try {
      if (pdf.data.state !== "completed") {
        throw new Error(`PDF failure: ${pdf.data.state}, ${pdf.data.job_id}`);
      }
      const { data, error } = await api.storage.from("temp").download(pdf.data.path);
      if (error) throw error;
      downloadBlob(data, `${pdf.data.name}.pdf`);
    } catch (error) {
      alerts.add({
        type: "danger",
        message: "Error downloading PDF",
      });
      console.log(error);
    }
    downloading = false;
  }
</script>

<SidebarSection>
  {#if doc?.data.thumbnail}
    {#key doc.data.thumbnail.path}
      <div class="thumbnail-container">
        <div class="relative aspect-square max-h-full max-w-full overflow-hidden border border-gray-300">
          {#key origDoc}
            <DocumentThumbnail doc={isVirtual ? origDoc : doc} />
          {/key}
        </div>
        <button
          class="absolute bottom-0 right-0 p-2"
          on:click={toggleThumbnailModal}
          use:tooltip={{ text: "Upload Image" }}>
          <UploadIcon />
        </button>
      </div>
    {/key}
  {/if}
  {#if isVirtual}
    <TextInput
      label="Name"
      {disabled}
      labelWidth="5.5rem"
      value={origDoc?.name}
      on:input={(e) => updateDoc("name", e.detail.value)} />
  {:else}
    <TextInput
      label="Name"
      {disabled}
      labelWidth="5.5rem"
      value={doc?.name}
      on:input={(e) => updateDoc("name", e.detail.value)} />
  {/if}
  <ButtonInput
    label="Count Grid"
    labelWidth="5.5rem"
    value={null}
    buttons={[{ component: GridIcon, label: "Show Count Grid", value: "grid" }]}
    on:input={() => eb.dispatch("toggle-count-grid")} />
  <ButtonInput
    label="Download PDF"
    disabled={downloading}
    labelWidth="5.5rem"
    value={null}
    buttons={[
      {
        component: downloading ? SpinnerIcon : DownloadIcon,
        spin: downloading,
        label: "Download PDF",
        value: "download",
      },
    ]}
    on:input={downloadDoc} />
</SidebarSection>

<PageList
  {group}
  {doc}
  {isVirtual}
  {docLocations}
  {disabled}
  filters={docFilters}
  bind:hoveredPage
  bind:lastSelectedPage
  on:zoom-to-page
  on:update-visible-pages />

{#if !isVirtual}
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <!-- svelte-ignore a11y-mouse-events-have-key-events -->
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <SidebarSection>
    <div class="relative">
      <div class="flex justify-between items-center">
        <h3 class="mb-2 px-2">Files</h3>
        <div class="flex gap-2 border border-transparent">
          {#if !$showDocFiles && files.length}
            <div>
              ({files.length})
            </div>
          {/if}
        </div>
      </div>
      <button class="absolute top-0 p-1 left-0 -ml-3 -mt-1" on:click={() => ($showDocFiles = !$showDocFiles)}>
        {#if $showDocFiles}
          <CaretDownIcon />
        {:else}
          <CaretRightIcon />
        {/if}
      </button>
    </div>
    {#if $showDocFiles}
      <div class="px-2">
        {#each files as file}
          <div
            class="flex justify-between items-center rounded file-button-container py-1"
            on:mouseover={() => (hoveredFile = file.name)}
            on:mouseout={() => (hoveredFile = null)}>
            <div
              class="flex gap-1 overflow-hidden"
              on:click={() => selectFile(file)}
              on:dblclick={() => dispatch("zoom-to-page", { page: pageIds[file.name] })}>
              <button
                class="flex-none text-gray-400 file-button"
                on:click|stopPropagation={() => downloadFile(file)}>
                <div class="file-button-icon">
                  <svelte:component this={fileIcons[extension(file.name)] || FileIcon} />
                </div>
                <div class="file-download-icon">
                  <DownloadIcon />
                </div>
              </button>
              <div class="grow overflow-hidden">
                <div class="truncate">
                  {file?.file.user_metadata?.name || file.name}
                </div>
              </div>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </SidebarSection>
{/if}

<!-- svelte-ignore a11y-mouse-events-have-key-events -->
{#if annotations?.length}
  <SidebarSection>
    <div class="relative">
      <div class="flex justify-between items-start">
        <h3 class="mb-2 px-2">Importable Shapes</h3>
        <div class="flex gap-2 border border-transparent">
          {#if $showDocAnnotations && annotations.length}
            <!-- <div>Page</div> -->
            <div class="pr-3.5 border-r border-transparent flex items-center gap-1">
              <button class="p-1 rounded hover:bg-gray-200" on:click={invertAnnotationSelection}>
                <InvertIcon />
              </button>
              <input
                bind:this={annoSelector}
                type="checkbox"
                bind:checked={overallAnnoSelection}
                on:input={selectAllAnnotations} />
            </div>
          {:else if annotations.length}
            <div>
              ({annotations.length})
            </div>
          {/if}
        </div>
      </div>
      <button
        class="absolute top-0 p-1 left-0 -ml-3 -mt-1"
        on:click={() => ($showDocAnnotations = !$showDocAnnotations)}>
        {#if $showDocAnnotations}
          <CaretDownIcon />
        {:else}
          <CaretRightIcon />
        {/if}
      </button>
    </div>
    {#if $showDocAnnotations}
      <div class="px-2">
        {#each annotations as annotation (annotation.id)}
          <button
            class="text-left w-full flex justify-between items-center rounded border hover:border-gray-400"
            class:border-transparent={parseInt(hoveredAnnotation) !== annotation.id}
            class:border-gray-400={parseInt(hoveredAnnotation) === annotation.id}
            on:mouseover={() => (hoveredAnnotation = annotation.id)}
            on:mouseout={() => (hoveredAnnotation = null)}
            on:click={() => dispatch("zoom-to-page", { page: annotation.page_id })}>
            <div
              class="p-1 flex gap-2 grow overflow-hidden annotation-item"
              class:selected={$selectedAnnotations[annotation.id]}>
              <div class="w-4 h-4 relative flex-none">
                <Viewport
                  padding={1}
                  drawing={liteDrawingMini(annotation.item, {
                    typeColor: $typeColors[annotation.item?.type_id],
                  })}
                  width={16}
                  height={16} />
              </div>
              <div class="grow flex gap-2 overflow-hidden">
                <div class="grow truncate">
                  {annotation.own_label}
                </div>
                <!-- <div class="text-gray-500">
                  {annotation.page}
                </div> -->
              </div>
              <div class="flex-none w-4">
                <input
                  type="checkbox"
                  checked={$selectedAnnotations[annotation.id]}
                  on:input={() => selectedAnnotations.toggle(annotation.id)}
                  on:click|stopPropagation />
              </div>
            </div>
          </button>
        {/each}
      </div>
      {#if !disabled}
        <div class="flex justify-end py-2">
          <button class="font-bold" on:click={beginConverting}>
            + Import Selected ({selectedAnnos.length})
          </button>
        </div>
      {/if}
    {/if}
  </SidebarSection>
{/if}

<Modal bind:this={thumbnailModal} closeable>
  <div slot="title">Upload</div>
  <div slot="content" class="space-y-2">
    <Dropzone on:drop={uploadThumbnail} bind:this={thumbnailDropzone} accept=".png,.jpeg,.jpg" />
  </div>
</Modal>

<Modal
  bind:this={convertModal}
  closeable
  width="36rem"
  on:confirm={convertAnnotations}
  fullframe
  buttons={selectedAnnos.length
    ? [
        { label: "Cancel", type: "cancel" },
        { label: "Create Items", type: "confirm", style: "primary" },
      ]
    : [{ label: "Cancel", type: "cancel" }]}>
  <div slot="title">Import Shapes</div>
  <div slot="content" class="overflow-y-auto p-4">
    {#if selectedAnnos.length}
      {@const hasLinearDims = selectedAnnos.some((a) => a.type === "/LineDimension")}
      {@const hasPolys = selectedAnnos.some((a) => a.type === "/PolygonDimension")}
      <div class="flex gap-1">
        <div class="relative">Import selected shapes as {lowerFirst(importAs)}</div>
        <button
          on:click={() => (showImportAs = !showImportAs)}
          class="rounded p-1 hover:bg-gray-200 relative">
          <CaretDownIcon />
          {#if showImportAs}
            <div class="dropdown-container text-right right-0">
              <button class="dropdown-button-item" on:click|stopPropagation={() => setImportAs("items")}
                >Items</button>
              <button class="dropdown-button-item" on:click|stopPropagation={() => setImportAs("openings")}
                >Openings</button>
            </div>
          {/if}
        </button>
      </div>
      <div class="space-y-1 mt-2">
        <div class="flex gap-2 px-4 justify-end text-xs text-gray-500">Page</div>
        {#each selectedAnnos as annotation}
          <div class="flex gap-2 px-4">
            <div class="w-4 h-4 relative">
              <Viewport padding={1} drawing={liteDrawingMini(annotation.item)} width={16} height={16} />
            </div>
            <div class="grow flex gap-2 items-center text-xs overflow-hidden">
              <div class="grow truncate">
                {annotation.own_label}
              </div>
              <div class="text-gray-500">
                {annotation.page + 1}
              </div>
            </div>
          </div>
        {/each}
      </div>
      <div class="mt-6 space-y-2">
        <TextInput label="Initial Mark" labelWidth="10.5rem" bind:value={initialMark} border />
        {#if hasLinearDims}
          <div class="space-y-2 py-2">
            <div class="text-xs font-bold">Linear Dimension Options:</div>
            <SelectInput
              border
              labelWidth="10.5rem"
              label="Imported Dimension is"
              bind:value={importedDimensionIs}
              options={[
                { label: "Width", value: "width" },
                { label: "Height", value: "height" },
              ]}
              on:input={updateAnnotationDimension} />
            <DimtextInput
              border
              labelWidth="10.5rem"
              label={importedDimensionIs === "width" ? "Height" : "Width"}
              bind:value={alternateDim}
              settings={$group.data.settings}
              on:input={updateAnnotationDimension} />
            <BooleanInput
              border
              labelWidth="10.5rem"
              label="Combine Duplicates"
              bind:value={coalesceLinearItems} />
          </div>
        {/if}
        {#if hasPolys}
          <div class="space-y-2 py-2">
            <div class="text-xs font-bold">Polygon Dimension Options:</div>
            <BooleanInput
              border
              labelWidth="10.5rem"
              label="Create clips from shapes"
              bind:value={createClipsFromImportedShapes} />
            <BooleanInput
              border
              labelWidth="10.5rem"
              label="Set clip as drawing"
              bind:value={setImportedClipAsDrawing}
              disabled={!createClipsFromImportedShapes} />
          </div>
        {/if}
      </div>
    {:else}
      <div>Select shapes to import as items.</div>
    {/if}
  </div>
</Modal>

<style lang="scss">
  .thumbnail-container {
    @apply h-36 py-2 relative overflow-hidden flex items-center justify-center;

    button {
      visibility: hidden;
    }

    &:hover {
      button {
        visibility: visible;
      }
    }
  }

  input.label-input {
    @apply grow bg-blue-200 text-black;
    border: none;
    outline: none;
    min-width: 0;
    max-width: 100%;
  }

  .file-button-container {
    .file-button-icon {
      display: block;
    }

    .file-download-icon {
      display: none;
    }

    &:hover {
      .file-button-icon {
        display: none;
      }

      .file-download-icon {
        display: block;
        color: black;
      }
    }
  }
</style>
